import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FieldValues } from 'react-hook-form';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import API from '../../utils/axios';
import { userSliceActions } from './userSlice';
import { adminSliceActions } from './adminSlice';

export interface authStateType {
	signinData: null | object;
	signupData: null | object;
	forgotpasswordData: null | object;
	userresetpasswordData: null | object;
	loading: boolean;
}
const initialState: authStateType = {
	signinData: null,
	signupData: null,
	forgotpasswordData: null,
	userresetpasswordData: null,
	loading: false,
};

//Api request for signin
export const signin = createAsyncThunk(
	'/auth/signin',
	async (formData: FieldValues, thunkApi) => {
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.post('/user/signin', formData);
			thunkApi.dispatch(authSliceActions.end_loading());
			thunkApi.dispatch(userSliceActions.clearCalculationData());
			thunkApi.dispatch(adminSliceActions.emptyUserList());
			!res.data?._id && toast.error(res.data.message);
			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request for signup
export const signup = createAsyncThunk(
	'/auth/signup',
	async (formData: FieldValues, thunkApi) => {
		thunkApi.dispatch(authSliceActions.start_loading());
		const res = await API.post('/user/signup', formData);
		thunkApi.dispatch(authSliceActions.end_loading());
		thunkApi.dispatch(userSliceActions.clearCalculationData());

		!res.data?.result
			? toast.error(res.data?.message)
			: toast.success(res.data?.message);
		return res.data;
	}
);

//Api request for Forgotpassword mail
export const forgotpassword = createAsyncThunk(
	'/auth/forgotpassword',
	async (formData: FieldValues, thunkApi) => {
		const res = await API.post('/user/forgotpassword', formData);
		return res.data;
	}
);

//Api request for resetpassword
export const userresetpassword = createAsyncThunk(
	'/auth/userresetpassword',
	async (formData: FieldValues, thunkApi) => {
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.post(
				`/user/reset-password/${formData.id}`,
				formData
			);
			thunkApi.dispatch(authSliceActions.end_loading());
			res.data?._id
				? toast.success(
						'Your password has been successfully changed!, please login again.'
				  )
				: toast.error(res.data?.message);
			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			return error;
		}
	}
);

//AuthSlice
const authSlice = createSlice({
	name: 'auth',
	initialState: initialState,
	extraReducers: (builder) => {
		builder.addCase(signin.fulfilled, (state: authStateType, action) => {
			state.signinData = action?.payload;
			localStorage.setItem(
				'profile',
				JSON.stringify({
					token: action?.payload?.token,
					email: action?.payload?.result?.email,
					role: action?.payload?.result?.roleId,
					fullName: `${action?.payload?.result?.firstName} ${action?.payload?.result?.lastName}`,
				})
			);
		});

		builder.addCase(signin.rejected, (state: authStateType, action: any) => {
			state.signinData = action?.payload;
		});

		builder.addCase(signup.fulfilled, (state: authStateType, action) => {
			state.signupData = action?.payload;
			localStorage.setItem(
				'profile',
				JSON.stringify({
					token: action?.payload?.token,
					email: action?.payload?.result?.email,
					role: action?.payload?.result?.roleId,
					fullName: `${action?.payload?.result?.firstName} ${action?.payload?.result?.lastName}`,
				})
			);
		});

		builder.addCase(forgotpassword.fulfilled, (state, action) => {
			state.forgotpasswordData = action?.payload;
		});

		builder.addCase(userresetpassword.fulfilled, (state, action) => {
			state.userresetpasswordData = action?.payload;
		});
		builder.addCase(userresetpassword.rejected, (state: any, action) => {
			state.userresetpasswordData = action?.payload;
		});
	},
	reducers: {
		signout: (state) => {
			state.signinData = null;
			localStorage.removeItem('profile');
		},
		start_loading: (state) => {
			state.loading = true;
		},
		end_loading: (state) => {
			state.loading = false;
		},
		clearForgotPasswordData: (state) => {
			state.forgotpasswordData = null;
		},
	},
});

export default authSlice;
export const authSliceActions = authSlice.actions;
